import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type MarketContext } from '../lib/api';
import { fmt } from '../lib/format';

type GateConfig = {
  manual_call_floor?: number;
  manual_put_ceiling?: number;
  manual_liq_updated_at?: string | null;
};

const STALE_TTL_MS = 24 * 60 * 60 * 1000;

export function LikuidasiPage() {
  const { data: ctxData, isLoading: ctxLoading, error: ctxError } = useQuery({
    queryKey: ['context', 'BTCUSDT'],
    queryFn: () => api.get<MarketContext>('/api/data/context?symbol=BTCUSDT'),
    refetchInterval: 60_000,
  });

  const { data: settingsData } = useQuery({
    queryKey: ['bot-settings'],
    queryFn: () => api.get<{ settings: Record<string, any> }>('/api/bot/settings'),
  });

  const spot = Number(ctxData?.spot_price);
  const s = settingsData?.settings || {};
  const floor = Number(s.manual_call_floor) || 0;
  const ceiling = Number(s.manual_put_ceiling) || 0;
  const updatedAt = s.manual_liq_updated_at as string | null;

  const hasValues = floor > 0 && ceiling > 0;
  const ageMs = updatedAt ? Date.now() - new Date(updatedAt).getTime() : null;
  const isFresh = hasValues && ageMs !== null && ageMs < STALE_TTL_MS;
  const ageHours = ageMs !== null ? Math.round(ageMs / (1000 * 60 * 60) * 10) / 10 : null;

  const status: 'fresh' | 'stale' | 'unset' = !hasValues ? 'unset' : !isFresh ? 'stale' : 'fresh';

  return (
    <div className="gl-page">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gl-text)' }}>Likuidasi</div>
        <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4, maxWidth: 640, lineHeight: 1.5 }}>
          <strong>Gate 2 — Manual Floor/Ceiling</strong>. Masukkan batas aman dari heatmap.
          Kalau data lebih dari 24 jam → <strong>fail-closed</strong> (semua kandidat ditolak).
        </div>
      </div>

      {ctxLoading && <div className="gl-empty">Memuat…</div>}
      {ctxError && <div className="settings-alert settings-alert--error">Gagal: {(ctxError as Error).message}</div>}

      <div className="g-card" style={{ marginBottom: 16, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>BTC spot</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt.number(spot, 0)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Status</div>
          <div style={{
            fontSize: 14, fontWeight: 600, marginTop: 4, padding: '4px 10px', borderRadius: 6,
            display: 'inline-block',
            background: status === 'fresh' ? 'rgba(52,211,153,0.15)' : status === 'stale' ? 'rgba(251,191,36,0.15)' : 'rgba(156,163,175,0.15)',
            color: status === 'fresh' ? 'var(--gl-emerald)' : status === 'stale' ? 'var(--gl-amber)' : 'var(--gl-text-muted)',
          }}>
            {status === 'fresh' ? '✓ Fresh' : status === 'stale' ? '⚠ Stale (>24h)' : '⊘ Belum di-set'}
          </div>
        </div>
      </div>

      {/* ── Floor / Ceiling values ── */}
      <div className="g-card" style={{ marginBottom: 16 }}>
        <div className="gl-label">Liquidation Boundaries</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginTop: 8 }}>
          <div style={{ padding: 14, background: 'var(--gl-bg)', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase', marginBottom: 4 }}>CALL safe floor</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gl-emerald)' }}>
              {floor > 0 ? `$${fmt.number(floor, 0)}` : '—'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gl-text-muted)', marginTop: 4 }}>
              Strike CALL harus di ATAS angka ini
            </div>
          </div>
          <div style={{ padding: 14, background: 'var(--gl-bg)', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase', marginBottom: 4 }}>PUT safe ceiling</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gl-rose)' }}>
              {ceiling > 0 ? `$${fmt.number(ceiling, 0)}` : '—'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gl-text-muted)', marginTop: 4 }}>
              Strike PUT harus di BAWAH angka ini
            </div>
          </div>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gl-text-muted)' }}>
          {updatedAt
            ? `Last updated: ${new Date(updatedAt).toLocaleString('id-ID')} (${ageHours} jam lalu)`
            : 'Belum pernah di-set. Isi di halaman Settings.'}
        </div>
      </div>

      <div style={{ marginTop: 16, fontSize: 12 }}>
        <Link to="/candidates" style={{ color: 'var(--accent-primary)' }}>Lihat Candidates (hasil Gate 2) →</Link>
      </div>
    </div>
  );
}
